if (!customElements.get('cm-looping-media-strip')) {
  customElements.define(
    'cm-looping-media-strip',
    class CmLoopingMediaStrip extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-cm-loop-viewport]');
        this.track = this.querySelector('[data-cm-loop-track]');
        this.originalGroup = this.querySelector('[data-cm-loop-group]');

        if (!this.viewport || !this.track || !this.originalGroup || !this.originalGroup.children.length) return;

        this.speed = Number.parseFloat(this.dataset.speed) || 1;
        this.layoutRun = 0;
        this.setAttribute('aria-busy', 'true');
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this.viewport);
        this.scheduleLayout();
      }

      disconnectedCallback() {
        this.layoutRun += 1;
        this.resizeObserver?.disconnect();
        window.cancelAnimationFrame(this.layoutFrame);
        this.pauseVideos(this.track);
      }

      scheduleLayout() {
        window.cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = window.requestAnimationFrame(() => this.layout());
      }

      async layout() {
        const layoutRun = ++this.layoutRun;
        const viewportWidth = this.viewport.clientWidth;
        if (!viewportWidth) return;

        this.classList.remove('is-ready');
        this.setAttribute('aria-busy', 'true');
        this.removeAttribute('data-media-error');
        this.removeGeneratedMedia();

        const styles = window.getComputedStyle(this);
        const gap = Number.parseFloat(styles.getPropertyValue('--cm-loop-gap')) || 0;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const visibleCards = isMobile ? 2.5 : 5.5;
        const visibleGaps = isMobile ? 2 : 5;
        const cardWidth = (viewportWidth - visibleGaps * gap) / visibleCards;

        this.style.setProperty('--cm-loop-card-width', `${Math.max(cardWidth, 1)}px`);

        const sourceItems = Array.from(this.originalGroup.children);
        let sourceIndex = 0;

        while (this.originalGroup.getBoundingClientRect().width < viewportWidth && sourceItems.length) {
          const clone = sourceItems[sourceIndex % sourceItems.length].cloneNode(true);
          clone.setAttribute('data-cm-loop-fill', '');
          clone.setAttribute('aria-hidden', 'true');
          this.cleanClone(clone);
          this.originalGroup.appendChild(clone);
          sourceIndex += 1;
        }

        const duplicateGroup = this.originalGroup.cloneNode(true);
        duplicateGroup.removeAttribute('data-cm-loop-group');
        duplicateGroup.setAttribute('data-cm-loop-copy', '');
        duplicateGroup.setAttribute('aria-hidden', 'true');
        this.cleanClone(duplicateGroup);
        this.track.appendChild(duplicateGroup);

        const distance = this.originalGroup.getBoundingClientRect().width;
        const renderedItemCount = this.originalGroup.children.length;
        const duration = (renderedItemCount * 6) / this.speed;

        this.style.setProperty('--cm-loop-distance', `${-distance}px`);
        this.style.setProperty('--cm-loop-duration', `${duration}s`);
        this.prepareImages(this.track);
        this.prepareVideos(this.track);

        const mediaReady = await this.waitForMedia(this.track);

        if (layoutRun !== this.layoutRun || !this.isConnected) return;

        this.setAttribute('aria-busy', 'false');

        if (!mediaReady) {
          this.setAttribute('data-media-error', '');
          return;
        }

        this.playVideos(this.track);
        void this.track.offsetWidth;
        this.classList.add('is-ready');
      }

      removeGeneratedMedia() {
        this.track.querySelectorAll('[data-cm-loop-copy]').forEach((copy) => {
          this.pauseVideos(copy);
          copy.remove();
        });

        this.originalGroup.querySelectorAll('[data-cm-loop-fill]').forEach((copy) => {
          this.pauseVideos(copy);
          copy.remove();
        });
      }

      cleanClone(element) {
        element.removeAttribute('data-shopify-editor-block');
        element.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });
        element.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      }

      prepareImages(root) {
        root.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          image.decoding = 'async';
          image.draggable = false;
        });
      }

      prepareVideos(root) {
        root.querySelectorAll('video').forEach((video) => {
          video.autoplay = true;
          video.controls = false;
          video.defaultMuted = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.preload = 'auto';
          video.setAttribute('aria-hidden', 'true');
          video.setAttribute('tabindex', '-1');

          if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) video.load();
        });
      }

      async waitForMedia(root) {
        const images = Array.from(root.querySelectorAll('img')).map((image) => this.waitForImage(image));
        const videos = Array.from(root.querySelectorAll('video')).map((video) => this.waitForVideo(video));
        const results = await Promise.all([...images, ...videos]);

        return results.every(Boolean);
      }

      waitForImage(image) {
        if (image.complete) {
          if (!image.naturalWidth) return Promise.resolve(false);
          return this.decodeImage(image);
        }

        return new Promise((resolve) => {
          const cleanup = () => {
            image.removeEventListener('load', handleLoad);
            image.removeEventListener('error', handleError);
          };
          const handleLoad = () => {
            cleanup();
            this.decodeImage(image).then(resolve);
          };
          const handleError = () => {
            cleanup();
            resolve(false);
          };

          image.addEventListener('load', handleLoad, { once: true });
          image.addEventListener('error', handleError, { once: true });
        });
      }

      async decodeImage(image) {
        if (typeof image.decode !== 'function') return image.complete && image.naturalWidth > 0;

        try {
          await image.decode();
          return true;
        } catch (_error) {
          return image.complete && image.naturalWidth > 0;
        }
      }

      waitForVideo(video) {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve(true);

        return new Promise((resolve) => {
          const cleanup = () => {
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('error', handleError);
          };
          const handleLoadedData = () => {
            cleanup();
            resolve(true);
          };
          const handleError = () => {
            cleanup();
            resolve(false);
          };

          video.addEventListener('loadeddata', handleLoadedData, { once: true });
          video.addEventListener('error', handleError, { once: true });
        });
      }

      playVideos(root) {
        root.querySelectorAll('video').forEach((video) => {
          video.play().catch(() => {});
        });
      }

      pauseVideos(root) {
        root?.querySelectorAll('video').forEach((video) => video.pause());
      }
    }
  );
}
